/**
 * ContextStatusRing — circular indicator for ADK auto-compaction state.
 *
 * Replaces the legacy manual "Compact history" button now that the backend
 * compacts post-invocation automatically. Pure status display — click is a
 * no-op.
 *
 * Visual contract:
 *   - h-8 w-8 outer container with square corners (design-system rule).
 *   - Background ring at low opacity (text-fg-faint).
 *   - Filled arc colour shifts at thresholds:
 *       <80%   → text-accent
 *       80-100% → text-warning
 *       >100%  → text-danger
 *   - Center label: tiny "%" number, spinner while loading, blank when
 *     the status is null or auto-compaction is disabled on the server.
 *   - Tooltip exposes the raw token counts + enabled state.
 */
import { cn } from "@/lib/cn";
import { useContextStatus } from "@/hooks/useContextStatus";

interface ContextStatusRingProps {
  sessionId: string | null;
  className?: string;
}

const SIZE = 32; // matches h-8 w-8
const STROKE = 3;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function colorForPercent(percent: number): string {
  if (percent > 100) return "text-danger";
  if (percent >= 80) return "text-warning";
  return "text-accent";
}

export function ContextStatusRing({ sessionId, className }: ContextStatusRingProps) {
  const { status, loading } = useContextStatus(sessionId);

  const percent = status?.percent_used ?? 0;
  const showRing = status !== null && status.enabled;
  // Clamp the visual arc at 100 even when the percent exceeds threshold so
  // the SVG geometry stays bounded; the colour already signals overflow.
  const clamped = Math.max(0, Math.min(percent, 100));
  const dashOffset = CIRCUMFERENCE * (1 - clamped / 100);
  const arcColor = colorForPercent(percent);

  const tooltip = status
    ? `Context: ${status.prompt_token_estimate}/${status.compaction_threshold} tokens (${status.percent_used.toFixed(1)}%) — auto-compaction ${status.enabled ? "on" : "off"}`
    : loading
      ? "Loading context status…"
      : "Context status unavailable";

  return (
    <div
      title={tooltip}
      aria-label={tooltip}
      className={cn(
        "relative flex h-8 w-8 items-center justify-center",
        className,
      )}
      style={{ borderRadius: 0 }}
    >
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="block"
      >
        {/* Background ring */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          strokeWidth={STROKE}
          className="text-fg-faint opacity-30"
          stroke="currentColor"
        />
        {/* Filled arc */}
        {showRing && (
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            strokeWidth={STROKE}
            strokeLinecap="butt"
            className={arcColor}
            stroke="currentColor"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        {loading && !status ? (
          <span
            className="inline-block h-2 w-2 animate-spin border border-fg-faint border-t-transparent"
            style={{ borderRadius: "9999px" }}
            aria-hidden
          />
        ) : showRing ? (
          <span className={cn("text-[8px] font-mono leading-none", arcColor)}>
            {Math.round(percent)}
          </span>
        ) : null}
      </div>
    </div>
  );
}
